import { toast } from "@devhop/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRealtimeConnected } from "@/app/providers/RealtimeProvider";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import {
  type CatalogCacheData,
  updateCatalogCacheAfterAdd,
} from "../lib/catalog-add";
import { feedsApi } from "./client";
import { feedDetailRealtimeRefetchInterval } from "./feed-detail-realtime";
import type { PostProductsContextResponse, SyncFeedsResponse } from "./types";

export type SyncFeedsInput = { pageId?: string };

export const feedsKeys = {
  all: ["feeds"] as const,
  list: (params?: {
    pageId?: string;
    feedType?: "post" | "live";
    q?: string;
    status?: "active" | "draft" | "closed" | "inactive";
    offset?: number;
    limit?: number;
  }) => ["feeds", "list", params] as const,
  detail: (id: string) => ["feeds", "detail", id] as const,
  catalog: (params?: {
    q?: string;
    pageId?: string;
    warehouseId?: string;
    offset?: number;
    limit?: number;
    groupByProduct?: boolean;
    includeUnavailable?: boolean;
    readiness?: "ready" | "zero" | "preorder";
    variantIds?: string[];
  }) => ["feeds", "catalog", params ?? {}] as const,
  defaultCodes: (q?: string) => ["feeds", "defaultCodes", q] as const,
  postContext: (feedId: string) => ["feeds", "postContext", feedId] as const,
  posWarehouses: () => ["feeds", "posWarehouses"] as const,
  detailCustomers: (feedId: string, q?: string) =>
    ["feeds", "detailCustomers", feedId, q] as const,
  detailProductSales: (feedId: string) =>
    [...feedsKeys.detail(feedId), "productSales"] as const,
  detailPaidProductSales: (feedId: string) =>
    [...feedsKeys.detail(feedId), "paidProductSales"] as const,
  detailProductMonitor: (feedId: string) =>
    [...feedsKeys.detail(feedId), "productMonitor"] as const,
  detailSessionProducts: (
    feedId: string,
    params: {
      offset: number;
      limit: number;
      saleEnabled?: boolean;
      q?: string;
    },
  ) =>
    [...feedsKeys.detail(feedId), "sessionProducts", "paged", params] as const,
  detailSessionProductsInfinite: (feedId: string) =>
    [...feedsKeys.detail(feedId), "sessionProducts", "infinite"] as const,
  detailComments: (feedId: string, q?: string) =>
    ["feeds", "detailComments", feedId, q] as const,
  messengerProfiles: (
    feedId: string,
    commentLogId: string,
    params: { q?: string; limit: number; offset: number },
  ) => ["feeds", "messengerProfiles", feedId, commentLogId, params] as const,
  orderSpecialWork: (feedId: string, orderId: string) =>
    ["feeds", "orderSpecialWork", feedId, orderId] as const,
  sessionPromo: (feedId: string) => ["feeds", "sessionPromo", feedId] as const,
  printFeeDefaults: () => ["feeds", "printFeeDefaults"] as const,
  sessionProductOptions: (feedId: string, spId: string) =>
    ["feeds", "sessionProductOptions", feedId, spId] as const,
  sessionProductOptionCandidates: (feedId: string, spId: string) =>
    ["feeds", "sessionProductOptionCandidates", feedId, spId] as const,
  preAddOptionCandidates: (feedId: string, variantId: string | null) =>
    ["feeds", "preAddOptionCandidates", feedId, variantId] as const,
};

/** ขนาดหน้าเริ่มต้นของรายการໂພສ (/app/feeds) */
export const FEEDS_PAGE_LIMIT = 20;
export const FEED_DETAIL_PAGE_LIMIT = 20;

export function useFeedsQuery(params?: {
  pageId?: string;
  feedType?: "post" | "live";
  q?: string;
  status?: "active" | "draft" | "closed" | "inactive";
  offset?: number;
  limit?: number;
}) {
  const offset = params?.offset ?? 0;
  const limit = params?.limit ?? FEEDS_PAGE_LIMIT;
  return useQuery({
    queryKey: feedsKeys.list({
      pageId: params?.pageId,
      feedType: params?.feedType,
      q: params?.q,
      status: params?.status,
      offset,
      limit,
    }),
    queryFn: () =>
      feedsApi.list({
        pageId: params?.pageId,
        feedType: params?.feedType ?? "post",
        q: params?.q,
        status: params?.status,
        offset,
        limit,
      }),
  });
}

export function useFeedDetailQuery(id: string) {
  const enabled = Boolean(id);
  const realtimeConnected = useRealtimeConnected();
  return useQuery({
    queryKey: feedsKeys.detail(id),
    queryFn: () => feedsApi.detailSummary(id),
    enabled,
    refetchInterval: feedDetailRealtimeRefetchInterval(
      realtimeConnected,
      enabled,
    ),
  });
}

export function useFeedCustomersQuery(
  feedId: string,
  q?: string,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: feedsKeys.detailCustomers(feedId, q),
    queryFn: ({ pageParam }) =>
      feedsApi.detailCustomers(feedId, {
        q,
        limit: FEED_DETAIL_PAGE_LIMIT,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.customers.length === 0) return undefined;
      const nextOffset = lastPage.offset + lastPage.customers.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: Boolean(feedId) && enabled,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.customers.some((row) => row.reminderDmStatus === "queued"),
      )
        ? 2_000
        : false,
    refetchIntervalInBackground: false,
  });
}

export function useAdjustFeedOrderQuantity(feedId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      orderId: string;
      quantity: number;
      idempotencyKey: string;
    }) =>
      feedsApi.adjustOrderQuantity(
        feedId,
        input.orderId,
        { quantity: input.quantity },
        input.idempotencyKey,
      ),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["feeds", "detailCustomers", feedId],
        }),
        queryClient.invalidateQueries({ queryKey: feedsKeys.detail(feedId) }),
        queryClient.invalidateQueries({
          queryKey: feedsKeys.detailProductMonitor(feedId),
        }),
        queryClient.invalidateQueries({
          queryKey: feedsKeys.detailProductSales(feedId),
        }),
        queryClient.invalidateQueries({
          queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
        }),
      ]);
      toast.success(
        result.order.status === "cancelled"
          ? `ຄືນ stock ${result.order.releasedQuantity} ຊິ້ນ ແລະ ຍົກເລີກລາຍການແລ້ວ`
          : `ຄືນ stock ${result.order.releasedQuantity} ຊິ້ນ ແລະ ປັບອໍເດີແລ້ວ`,
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? formatCfApiErrorMessage(error.message)
          : "ປັບຈຳນວນບໍ່ສຳເລັດ",
      );
    },
  });
}

export function useFeedProductSalesQuery(feedId: string, enabled = true) {
  const active = Boolean(feedId) && enabled;
  const realtimeConnected = useRealtimeConnected();
  return useInfiniteQuery({
    queryKey: feedsKeys.detailProductSales(feedId),
    queryFn: ({ pageParam }) =>
      feedsApi.detailProductSales(feedId, {
        limit: FEED_DETAIL_PAGE_LIMIT,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.products.length === 0) return undefined;
      const nextOffset = lastPage.offset + lastPage.products.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: active,
    refetchInterval: feedDetailRealtimeRefetchInterval(
      realtimeConnected,
      active,
    ),
    refetchIntervalInBackground: false,
  });
}

export function useFeedPaidProductSalesQuery(feedId: string, enabled = true) {
  const active = Boolean(feedId) && enabled;
  const realtimeConnected = useRealtimeConnected();
  return useInfiniteQuery({
    queryKey: feedsKeys.detailPaidProductSales(feedId),
    queryFn: ({ pageParam }) =>
      feedsApi.detailProductSales(feedId, {
        limit: FEED_DETAIL_PAGE_LIMIT,
        offset: pageParam,
        paymentState: "paid",
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.products.length === 0) return undefined;
      const nextOffset = lastPage.offset + lastPage.products.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: active,
    refetchInterval: feedDetailRealtimeRefetchInterval(
      realtimeConnected,
      active,
    ),
    refetchIntervalInBackground: false,
  });
}

/** soft poll — central remaining อาจเปลี่ยนจาก Admin/POS โดยไม่มี CF SSE */
const PRODUCT_MONITOR_SOFT_POLL_MS = 60_000;
const PRODUCT_MONITOR_PAGE_LIMIT = 10;
const SESSION_PRODUCTS_PAGE_LIMIT = 10;

export function useFeedProductMonitorQuery(
  feedId: string,
  enabled = true,
  productIds?: string[],
  refetchIntervalMs = PRODUCT_MONITOR_SOFT_POLL_MS,
) {
  const active = Boolean(feedId) && enabled;
  return useInfiniteQuery({
    queryKey:
      productIds === undefined
        ? feedsKeys.detailProductMonitor(feedId)
        : [...feedsKeys.detailProductMonitor(feedId), { productIds }],
    queryFn: ({ pageParam }) =>
      feedsApi.detailProductMonitor(feedId, {
        limit: PRODUCT_MONITOR_PAGE_LIMIT,
        offset: pageParam,
        productIds,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.products.length === 0) return undefined;
      const nextOffset = lastPage.offset + lastPage.products.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: active,
    refetchInterval: active ? refetchIntervalMs : false,
    refetchIntervalInBackground: productIds !== undefined,
  });
}

export function useOrderSpecialWorkQuery(
  feedId: string,
  orderId: string | null,
) {
  return useQuery({
    queryKey: feedsKeys.orderSpecialWork(feedId, orderId ?? ""),
    queryFn: () => feedsApi.getOrderSpecialWork(feedId, orderId as string),
    enabled: Boolean(feedId && orderId),
  });
}

export function useUpdateOrderSpecialWork(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      orderId: string;
      units: Array<{
        unitIndex: number;
        name?: string | null;
        number?: string | null;
      }>;
    }) => feedsApi.updateOrderSpecialWork(feedId, input.orderId, input.units),
    onSuccess: (data) => {
      qc.setQueryData(feedsKeys.orderSpecialWork(feedId, data.orderId), data);
      void qc.invalidateQueries({
        queryKey: [...feedsKeys.detail(feedId), "productSales"],
      });
      toast.success("ບັນທຶກຂໍ້ມູນງານພິມແລ້ວ");
    },
    onError: () => toast.error("ບັນທຶກຂໍ້ມູນງານພິມບໍ່ສຳເລັດ"),
  });
}

export function useFeedSessionProductsInfiniteQuery(
  feedId: string,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
    // POS อาจเปลี่ยน preorder นอก CF; refresh เฉพาะหน้าที่เปิดอยู่
    refetchInterval: enabled ? 15_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: ({ pageParam }) =>
      feedsApi.detailSessionProducts(feedId, {
        limit: SESSION_PRODUCTS_PAGE_LIMIT,
        offset: pageParam,
        groupByProduct: true,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const pageLen = lastPage.products?.length ?? lastPage.items.length;
      if (pageLen === 0) return undefined;
      const nextOffset = lastPage.offset + pageLen;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: Boolean(feedId) && enabled,
  });
}

export function useFeedSessionProductsDetailQuery(
  feedId: string,
  params: {
    offset: number;
    limit: number;
    saleEnabled?: boolean;
    q?: string;
  },
  enabled = true,
) {
  return useQuery({
    queryKey: feedsKeys.detailSessionProducts(feedId, params),
    queryFn: () => feedsApi.detailSessionProducts(feedId, params),
    enabled: Boolean(feedId) && enabled,
    placeholderData: (previousData) => previousData,
  });
}

export function usePrintFeeDefaultsQuery() {
  return useQuery({
    queryKey: feedsKeys.printFeeDefaults(),
    queryFn: () => feedsApi.getPrintFeeDefaults(),
  });
}

export function usePutPrintFeeDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      items: Array<{ optionCode: string; defaultSurchargeLak: number }>,
    ) => feedsApi.putPrintFeeDefaults(items),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: feedsKeys.printFeeDefaults() });
      void qc.invalidateQueries({ queryKey: ["feeds", "sessionPromo"] });
      toast.success("ບັນທຶກຄ່າພິມເລີ່ມຕົ້ນແລ້ວ");
    },
    onError: () => {
      toast.error("ບັນທຶກຄ່າພິມເລີ່ມຕົ້ນລົ້ມເຫຼວ");
    },
  });
}

export function useSessionPromoQuery(feedId: string) {
  return useQuery({
    queryKey: feedsKeys.sessionPromo(feedId),
    queryFn: () => feedsApi.getSessionPromo(feedId),
    enabled: Boolean(feedId),
  });
}

export function usePatchSessionPromo(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      listDiscountPercent?: number | null;
      listDiscountFixedLak?: number | null;
      freeShipping?: boolean;
      shippingFeeLak?: number | null;
      printOptions?: Array<{
        code: string;
        enabled: boolean;
        surchargeLak: number;
        isAutoAttach?: boolean;
        requiresCustomerInput?: boolean;
      }>;
    }) => feedsApi.patchSessionPromo(feedId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: feedsKeys.sessionPromo(feedId) });
      void qc.invalidateQueries({
        queryKey: ["feeds", "sessionProductOptions"],
      });
      void qc.invalidateQueries({
        queryKey: ["feeds", "sessionProductOptionCandidates"],
      });
      void qc.invalidateQueries({
        queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
      });
      toast.success("ບັນທຶກໂປຣໂພສແລ້ວ");
    },
    onError: (err: Error) => {
      const msg = err.message;
      if (msg.includes("INVALID_DISCOUNT_PERCENT")) {
        toast.error("ເປີເຊັນສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ");
        return;
      }
      if (msg.includes("NO_SESSION_PRODUCTS")) {
        toast.error("ເພີ່ມສິນຄ້າໃນໂພສກ່ອນຕັ້ງຊື່ / ໝາຍເລກ");
        return;
      }
      if (msg.includes("OPTION_NOT_FOUND")) {
        toast.error("ຍັງບໍ່ພົບ option ຊື່ / ໝາຍເລກ ໃນ POS (ຮັນ seed)");
        return;
      }
      toast.error("ບັນທຶກໂປຣໂພສລົ້ມເຫຼວ");
    },
  });
}

export function useSessionProductOptionsQuery(
  feedId: string,
  spId: string | null,
) {
  return useQuery({
    queryKey: feedsKeys.sessionProductOptions(feedId, spId ?? ""),
    queryFn: () => feedsApi.listSessionProductOptions(feedId, spId ?? ""),
    enabled: Boolean(feedId && spId),
  });
}

export function useSessionProductOptionCandidatesQuery(
  feedId: string,
  spId: string | null,
) {
  return useQuery({
    queryKey: feedsKeys.sessionProductOptionCandidates(feedId, spId ?? ""),
    queryFn: () =>
      feedsApi.listSessionProductOptionCandidates(feedId, spId ?? ""),
    enabled: Boolean(feedId && spId),
  });
}

export function usePreAddOptionCandidatesQuery(
  feedId: string,
  variantId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: feedsKeys.preAddOptionCandidates(feedId, variantId),
    queryFn: () => feedsApi.listPreAddOptionCandidates(feedId, variantId),
    enabled: Boolean(feedId) && enabled,
  });
}

export function usePutSessionProductOptions(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      spId: string;
      items: Array<{
        adminOptionDefinitionId: string;
        surchargeLak: number;
        isAutoAttach?: boolean;
        requiresCustomerInput?: boolean;
        sortOrder?: number;
      }>;
    }) => feedsApi.putSessionProductOptions(feedId, input.spId, input.items),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.sessionProductOptions(feedId, vars.spId),
      });
      void qc.invalidateQueries({
        queryKey: feedsKeys.sessionProductOptionCandidates(feedId, vars.spId),
      });
      void qc.invalidateQueries({
        queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
      });
      toast.success("ບັນທຶກ option ແລ້ວ");
    },
    onError: (err: Error) => {
      if (err.message.includes("OPTION_NOT_MAPPED_TO_PRODUCT")) {
        toast.error("option ນີ້ບໍ່ຖືກຜູກກັບສິນຄ້າໃນ POS");
        return;
      }
      toast.error("ບັນທຶກ option ລົ້ມເຫຼວ");
    },
  });
}

export function useFeedCommentsQuery(
  feedId: string,
  params: { q?: string },
  enabled = true,
) {
  const active = Boolean(feedId) && enabled;
  return useInfiniteQuery({
    queryKey: feedsKeys.detailComments(feedId, params.q),
    queryFn: ({ pageParam }) =>
      feedsApi.detailComments(feedId, {
        q: params.q,
        limit: FEED_DETAIL_PAGE_LIMIT,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.comments.length === 0) return undefined;
      const nextOffset = lastPage.offset + lastPage.comments.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: active,
  });
}

export function useCommentMessengerProfilesQuery(
  feedId: string,
  commentLogId: string,
  params: { q?: string; limit: number; offset: number },
  enabled = true,
) {
  return useQuery({
    queryKey: feedsKeys.messengerProfiles(feedId, commentLogId, params),
    queryFn: () =>
      feedsApi.messengerProfiles(feedId, commentLogId, {
        q: params.q,
        limit: params.limit,
        offset: params.offset,
      }),
    enabled: Boolean(feedId && commentLogId) && enabled,
  });
}

export function useLinkCommentMessengerProfile(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { commentLogId: string; profileId: string }) =>
      feedsApi.linkCommentMessengerProfile(
        feedId,
        input.commentLogId,
        input.profileId,
      ),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: ["feeds", "detailComments", feedId],
      });
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailCustomers(feedId),
      });
      toast.success("ເຊື່ອມລູກຄ້າ Messenger ແລ້ວ", {
        description:
          data.notificationStatus === "sent"
            ? "ຂໍ້ຄວາມຂອງອໍເດີ້ນີ້ຖືກສົ່ງແລ້ວ"
            : "ລະບົບກຳລັງສົ່ງຂໍ້ຄວາມໃຫ້ລູກຄ້າ",
      });
    },
    onError: (error: Error) => {
      toast.error(formatCfApiErrorMessage(error.message));
    },
  });
}

export function usePatchFeedSessionProduct(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      spId: string;
      saleEnabled?: boolean;
      allocatedQuantity?: number | null;
      allocationMode?: "limit" | "unlimited";
      discountPercent?: number | null;
      discountFixedLak?: number | null;
    }) =>
      feedsApi.patchFeedSessionProduct(feedId, input.spId, {
        saleEnabled: input.saleEnabled,
        allocatedQuantity: input.allocatedQuantity,
        allocationMode: input.allocationMode,
        discountPercent: input.discountPercent,
        discountFixedLak: input.discountFixedLak,
      }),
    onSuccess: (_data, vars) => {
      if (vars.allocationMode != null) {
        toast.success("ອັບເດດໂຫມດໂຄວຕ້າແລ້ວ");
      } else if (
        vars.discountPercent !== undefined ||
        vars.discountFixedLak !== undefined
      ) {
        toast.success("ອັບເດດສ່ວນຫຼຸດສິນຄ້າແລ້ວ");
      } else {
        toast.success("ອັບເດດສິນຄ້າແລ້ວ");
      }
      return qc.invalidateQueries({ queryKey: feedsKeys.detail(feedId) });
    },
    onError: (error: Error) =>
      mapSessionProductPatchError(error, "ອັບເດດສິນຄ້າລົ້ມເຫຼວ"),
  });
}

export function useCreateManualOrder(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      commentLogId?: string;
      cfCode: string;
      quantity: number;
      specialWorkUnits?: Array<{
        unitIndex: number;
        name?: string | null;
        number?: string | null;
      }>;
    }) => feedsApi.createManualOrder(feedId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: feedsKeys.detail(feedId) });
      qc.invalidateQueries({ queryKey: feedsKeys.detailCustomers(feedId) });
      qc.invalidateQueries({
        queryKey: ["feeds", "detailComments", feedId],
      });
      qc.invalidateQueries({
        queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
      });
      if (data.order.dmStatus === "failed") {
        toast.error("ສ້າງອໍເດີ້ແລ້ວ ແຕ່ສົ່ງຂໍ້ຄວາມລູກຄ້າບໍ່ສຳເລັດ", {
          description: "ກວດ template / page token ແລ້ວລອງສົ່ງຄືນຈາກແທັບລູກຄ້າ",
        });
      } else if (data.order.dmStatus === "skipped") {
        toast.warning("ສ້າງອໍເດີ້ແລ້ວ ແຕ່ຍັງບໍ່ສົ່ງຂໍ້ຄວາມ", {
          description: data.order.dmSkipReason ?? undefined,
        });
      } else if (data.order.incrementedExisting) {
        toast.success("ເພີ່ມຈຳນວນໃນອໍເດີ້ເດີມແລ້ວ", {
          description: data.order.shopOrderCode
            ? `ບິນ ${data.order.shopOrderCode} · ລວມ ${data.order.quantity} ຊິ້ນ`
            : `ລວມ ${data.order.quantity} ຊິ້ນ`,
        });
      } else if (data.order.newOrderAfterPosLocked) {
        toast.success("ສ້າງອໍເດີ້ໃໝ່ແລ້ວ", {
          description: data.order.shopOrderCode
            ? `ບິນ ${data.order.shopOrderCode} (ບິນເກົ່າຢືນຢັນແລ້ວ)`
            : undefined,
        });
      } else if (data.order.linkedExisting) {
        toast.success("ເຊື່ອມຄອມເມັນກັບອໍເດີ້ເດີມແລ້ວ", {
          description: data.order.shopOrderCode
            ? `ບິນ ${data.order.shopOrderCode}`
            : undefined,
        });
      } else if (data.order.partial) {
        toast.success("ສ້າງອໍເດີ້ແລ້ວ (ຈຳນວນບາງສ່ວນ)", {
          description: `ຂໍ ${data.order.requestedQuantity} ແຕ່ຈອງໄດ້ ${data.order.quantity}`,
        });
      } else {
        toast.success(
          data.order.dmStatus === "sent"
            ? "ສ້າງອໍເດີ້ແລ້ວ ແລະສົ່ງຂໍ້ຄວາມລູກຄ້າແລ້ວ"
            : "ສ້າງອໍເດີ້ແລ້ວ",
        );
      }
    },
    /** error แสดงใน ManualOrderDialog — ไม่ toast ซ้ำ */
    onError: () => {},
  });
}

export function useCreateManualOrders(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      commentLogIds: string[];
      cfCode: string;
      quantity: number;
      specialWorkUnits?: Array<{
        unitIndex: number;
        name?: string | null;
        number?: string | null;
      }>;
    }) => feedsApi.createManualOrders(feedId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: feedsKeys.detail(feedId) });
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailCustomers(feedId),
      });
      void qc.invalidateQueries({
        queryKey: ["feeds", "detailComments", feedId],
      });
      void qc.invalidateQueries({
        queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
      });

      if (data.failed.length > 0) {
        toast.warning(
          `ສ້າງສຳເລັດ ${data.orders.length} · ບໍ່ສຳເລັດ ${data.failed.length}`,
        );
      } else {
        toast.success(`ສ້າງອໍເດີ້ ${data.orders.length} ລາຍການແລ້ວ`);
      }
    },
    /** error แสดงใน ManualOrderDialog */
    onError: () => {},
  });
}

export function useSyncFeeds() {
  const qc = useQueryClient();
  return useMutation<SyncFeedsResponse, Error, SyncFeedsInput | undefined>({
    mutationFn: (input) => feedsApi.sync(input?.pageId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: feedsKeys.all });
      const total = data.results.reduce((sum, row) => sum + row.upserted, 0);
      toast.success("ດຶງໂພສຈາກ Facebook ສໍາເລັດ", {
        description: `ອັບເດດ ${total} ໂພສ`,
      });
    },
    onError: () => toast.error("ດຶງໂພສລົ້ມເຫຼວ"),
  });
}

const CATALOG_PAGE_LIMIT = 20;

export function useCatalogQuery(params?: {
  q?: string;
  pageId?: string;
  warehouseId?: string;
  offset?: number;
  limit?: number;
  groupByProduct?: boolean;
  includeUnavailable?: boolean;
  readiness?: "ready" | "zero" | "preorder";
  variantIds?: string[];
  enabled?: boolean;
}) {
  const offset = params?.offset ?? 0;
  const limit = params?.limit ?? CATALOG_PAGE_LIMIT;
  return useQuery({
    queryKey: feedsKeys.catalog({
      q: params?.q,
      pageId: params?.pageId,
      warehouseId: params?.warehouseId,
      offset,
      limit,
      groupByProduct: params?.groupByProduct,
      includeUnavailable: params?.includeUnavailable,
      readiness: params?.readiness,
      variantIds: params?.variantIds,
    }),
    queryFn: () =>
      feedsApi.catalog({
        pageId: params?.pageId,
        warehouseId: params?.warehouseId,
        q: params?.q,
        offset,
        limit,
        groupByProduct: params?.groupByProduct,
        includeUnavailable: params?.includeUnavailable,
        readiness: params?.readiness,
        variantIds: params?.variantIds,
      }),
    enabled: params?.enabled ?? true,
  });
}

export function useCatalogInfiniteQuery(params?: {
  q?: string;
  pageId?: string;
  warehouseId?: string;
  limit?: number;
  groupByProduct?: boolean;
  includeUnavailable?: boolean;
  readiness?: "ready" | "zero" | "preorder";
  enabled?: boolean;
}) {
  const limit = params?.limit ?? CATALOG_PAGE_LIMIT;
  return useInfiniteQuery({
    queryKey: feedsKeys.catalog({
      q: params?.q,
      pageId: params?.pageId,
      warehouseId: params?.warehouseId,
      limit,
      groupByProduct: params?.groupByProduct,
      includeUnavailable: params?.includeUnavailable,
      readiness: params?.readiness,
    }),
    queryFn: ({ pageParam }) =>
      feedsApi.catalog({
        pageId: params?.pageId,
        warehouseId: params?.warehouseId,
        q: params?.q,
        offset: pageParam,
        limit,
        groupByProduct: params?.groupByProduct,
        includeUnavailable: params?.includeUnavailable,
        readiness: params?.readiness,
      }),
    enabled: params?.enabled ?? true,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const pageLength = lastPage.products?.length ?? lastPage.items.length;
      if (pageLength === 0) return undefined;
      const loaded = pages.reduce(
        (sum, page) => sum + (page.products?.length ?? page.items.length),
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
}

export function usePosWarehousesQuery() {
  const { user } = useAuthState();
  return useQuery({
    queryKey: [...feedsKeys.posWarehouses(), user?.id],
    queryFn: () => feedsApi.listPosWarehouses(),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export { CATALOG_PAGE_LIMIT };

export function useDefaultCodesQuery(
  q?: string,
  params?: { groupByProduct?: boolean; limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: [
      ...feedsKeys.defaultCodes(q),
      params?.groupByProduct,
      params?.limit,
      params?.offset,
    ],
    queryFn: () =>
      feedsApi.defaultCodes({
        q,
        limit: params?.limit ?? 20,
        offset: params?.offset ?? 0,
        groupByProduct: params?.groupByProduct,
      }),
  });
}

export function useReverseVariantCfStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) =>
      feedsApi.reverseVariantCfStock(variantId),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: feedsKeys.defaultCodes() });
      void qc.invalidateQueries({ queryKey: ["feeds", "catalog"] });
      void qc.invalidateQueries({ queryKey: ["live"] });
      toast.success("ຄືນເຂົ້າສະຕັອກຫຼັກແລ້ວ", {
        description: `ປລອຍ ${data.releasedQuantity} ຊິ້ນ`,
      });
    },
    onError: (err: Error) => {
      if (err.message.includes("NOTHING_TO_RELEASE")) {
        toast.error("ບໍ່ມີຈຳນວນຈອງໃນ CF ທີ່ຄືນໄດ້");
        return;
      }
      toast.error("ຄືນເຂົ້າສະຕັອກຫຼັກລົ້ມເຫຼວ", {
        description: err.message,
      });
    },
  });
}

export function useSetDefaultCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { variantId: string; cfCode: string }) =>
      feedsApi.setDefaultCode(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedsKeys.defaultCodes() });
      qc.invalidateQueries({ queryKey: ["feeds", "catalog"] });
      toast.success("ບັນທຶກລະຫັດ CF ແລ້ວ");
    },
    onError: (err: Error) => {
      const code = err.message;
      if (code.includes("CF_CODE_TAKEN")) {
        toast.error("ລະຫັດ CF ນີ້ຖືກໃຊ້ແລ້ວ");
      } else if (code.includes("INVALID_CF_CODE")) {
        toast.error("ລະຫັດ CF ບໍ່ຖືກຕ້ອງ");
      } else {
        toast.error("ບັນທຶກລະຫັດ CF ລົ້ມເຫຼວ");
      }
    },
  });
}

export function usePostProductsContext(feedId?: string) {
  return useQuery({
    queryKey: feedsKeys.postContext(feedId ?? ""),
    queryFn: () =>
      feedId
        ? feedsApi.postProductsContext(feedId)
        : Promise.reject(new Error("FEED_ID_REQUIRED")),
    enabled: Boolean(feedId),
  });
}

export function usePatchSessionWarehouse(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (warehouseId: string) =>
      feedsApi.patchSessionWarehouse({ feedId, warehouseId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: feedsKeys.postContext(feedId) });
      void qc.invalidateQueries({ queryKey: ["feeds", "catalog"] });
      toast.success("ອັບເດດຄັງສິນຄ້າແລ້ວ");
    },
    onError: (err: Error) => {
      if (err.message.includes("SESSION_NOT_EDITABLE")) {
        toast.error("ບໍ່ສາມາດປ່ຽນຄັງໄດ້ຫຼັງຢືນຢັນໂພສ");
        return;
      }
      toast.error("ອັບເດດຄັງສິນຄ້າລົ້ມເຫຼວ");
    },
  });
}

export function useAddSessionProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      feedId: string;
      variantId: string;
      cfCode?: string;
      price?: number;
      allocatedQuantity?: number;
      allocationMode?: "limit" | "unlimited";
      options?: Array<{
        adminOptionDefinitionId: string;
        surchargeLak: number;
        isAutoAttach?: boolean;
        requiresCustomerInput?: boolean;
        sortOrder?: number;
      }>;
      discountPercent?: number | null;
      discountFixedLak?: number | null;
    }) => feedsApi.addSessionProduct(body),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(vars.feedId),
      });
      qc.setQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(vars.feedId),
        (old) =>
          old
            ? {
                ...old,
                sessionProducts: old.sessionProducts.some(
                  (item) => item.id === data.item.id,
                )
                  ? old.sessionProducts
                  : [...old.sessionProducts, data.item],
              }
            : old,
      );
      const context = qc.getQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(vars.feedId),
      );
      if (context) {
        qc.setQueriesData<CatalogCacheData>(
          {
            queryKey: [
              "feeds",
              "catalog",
              {
                pageId: context.feed.pageId,
                warehouseId: context.session.retailStockWarehouseId,
              },
            ],
          },
          (old) => updateCatalogCacheAfterAdd(old, [data.item]),
        );
      }
      toast.success("ເພີ່ມສິນຄ້າໃນໂພສແລ້ວ");
    },
    onError: (err: Error) => {
      const code = err.message;
      if (code.includes("PRODUCT_ALREADY_IN_SESSION")) {
        toast.error("ສິນຄ້ານີ້ຢູ່ໃນໂພສແລ້ວ");
      } else if (code.includes("DEFAULT_CF_CODE_REQUIRED")) {
        toast.error("ສິນຄ້ານີ້ຍັງບໍ່ມີລະຫັດ CF ເລີ່ມຕົ້ນ");
      } else if (code.includes("INVALID_CF_CODE")) {
        toast.error("ລະຫັດ CF ບໍ່ຖືກຕ້ອງ (ສູງສຸດ 10 ຕົວ)");
      } else if (code.includes("CF_CODE_TAKEN")) {
        toast.error("ລະຫັດ CF ນີ້ຖືກໃຊ້ໃນໂພສແລ້ວ");
      } else if (code.includes("QUANTITY_EXCEEDS_STOCK")) {
        toast.error("ຈຳນວນເກີນສະຕັອກຄົງເຫຼືອ");
      } else if (code.includes("QUANTITY_EXCEEDS_AVAILABLE_STOCK")) {
        toast.error("ຈຳນວນເກີນສະຕັອກທີ່ແບ່ງໄດ້ (ມີການຈອງໃນໂພສອື່ນ)");
      } else if (code.includes("INVALID_QUANTITY")) {
        toast.error("ຈຳນວນບໍ່ຖືກຕ້ອງ");
      } else if (code.includes("INVALID_PRICE")) {
        toast.error("ລາຄາບໍ່ຖືກຕ້ອງ");
      } else {
        toast.error("ເພີ່ມສິນຄ້າລົ້ມເຫຼວ");
      }
    },
  });
}

export function useAddSessionProductsBySelection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      feedId: string;
      allocationMode?: "limit" | "unlimited";
      allocatedQuantity?: number;
      selection:
        | { mode: "selected"; variantIds: string[] }
        | {
            mode: "all_matching";
            q?: string;
            excludedVariantIds?: string[];
          };
      options?: Array<{
        adminOptionDefinitionId: string;
        surchargeLak: number;
        isAutoAttach?: boolean;
        requiresCustomerInput?: boolean;
        sortOrder?: number;
      }>;
      discountPercent?: number | null;
      discountFixedLak?: number | null;
    }) => feedsApi.addSessionProductsBySelection(body),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(vars.feedId),
      });
      qc.setQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(vars.feedId),
        (old) => {
          if (!old) return old;
          const existingIds = new Set(
            old.sessionProducts.map((item) => item.id),
          );
          const addedItems = data.items.filter(
            (item) => !existingIds.has(item.id),
          );
          return {
            ...old,
            sessionProducts: [...old.sessionProducts, ...addedItems],
          };
        },
      );
      const context = qc.getQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(vars.feedId),
      );
      if (context) {
        qc.setQueriesData<CatalogCacheData>(
          {
            queryKey: [
              "feeds",
              "catalog",
              {
                pageId: context.feed.pageId,
                warehouseId: context.session.retailStockWarehouseId,
              },
            ],
          },
          (old) => updateCatalogCacheAfterAdd(old, data.items),
        );
      }
      if (data.failed.length > 0) {
        toast.warning(
          `ເພີ່ມ ${data.added} · ຂ້າມ ${data.skipped} · ບໍ່ສຳເລັດ ${data.failed.length}`,
        );
      } else {
        toast.success(`ເພີ່ມ ${data.added} · ຂ້າມ ${data.skipped}`);
      }
    },
    onError: () => toast.error("ເພີ່ມສິນຄ້າຫຼາຍລາຍການບໍ່ສຳເລັດ"),
  });
}

function mapSessionProductPatchError(err: Error, fallback: string) {
  const code = err.message;
  if (code.includes("CF_CODE_TAKEN")) {
    toast.error("ລະຫັດ CF ນີ້ຖືກໃຊ້ໃນໂພສແລ້ວ");
  } else if (code.includes("INVALID_CF_CODE")) {
    toast.error("ລະຫັດ CF ບໍ່ຖືກຕ້ອງ (ສູງສຸດ 10 ຕົວ)");
  } else if (code.includes("ALLOCATION_BELOW_SOLD")) {
    toast.error("ໂຄວຕ້າຕ້ອງບໍ່ຕ່ຳກວ່າຈຳນວນທີ່ຮັບ CF ແລ້ວ");
  } else if (code.includes("QUANTITY_EXCEEDS_STOCK")) {
    toast.error("ຈຳນວນເກີນສະຕັອກຄົງເຫຼືອ");
  } else if (code.includes("QUANTITY_EXCEEDS_AVAILABLE_STOCK")) {
    toast.error("ຈຳນວນເກີນສະຕັອກທີ່ແບ່ງໄດ້ (ມີການຈອງໃນໂພສອື່ນ)");
  } else if (code.includes("INVALID_QUANTITY")) {
    toast.error("ຈຳນວນບໍ່ຖືກຕ້ອງ");
  } else if (code.includes("INVALID_PRICE")) {
    toast.error("ລາຄາບໍ່ຖືກຕ້ອງ");
  } else {
    toast.error(fallback);
  }
}

export function usePatchSessionProduct(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      allocatedQuantity?: number;
      cfCode?: string;
      price?: number;
      allocationMode?: "limit" | "unlimited";
      discountPercent?: number | null;
      discountFixedLak?: number | null;
      saleEnabled?: boolean;
    }) =>
      feedsApi.patchPostSessionProduct(input.id, {
        allocatedQuantity: input.allocatedQuantity,
        cfCode: input.cfCode,
        price: input.price,
        allocationMode: input.allocationMode,
        discountPercent: input.discountPercent,
        discountFixedLak: input.discountFixedLak,
        saleEnabled: input.saleEnabled,
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(feedId),
        (old) =>
          old
            ? {
                ...old,
                sessionProducts: old.sessionProducts.map((item) =>
                  item.id === data.item.id ? data.item : item,
                ),
              }
            : old,
      );
      if (vars.cfCode != null) {
        toast.success("ອັບເດດລະຫັດ CF ແລ້ວ");
      } else if (vars.price != null) {
        toast.success("ອັບເດດລາຄາແລ້ວ");
      } else if (
        vars.discountPercent !== undefined ||
        vars.discountFixedLak !== undefined
      ) {
        toast.success("ອັບເດດສ່ວນຫຼຸດສິນຄ້າແລ້ວ");
      } else if (vars.allocationMode != null) {
        toast.success("ອັບເດດໂຫມດໂຄວຕ້າແລ້ວ");
      } else if (vars.saleEnabled !== undefined) {
        toast.success(vars.saleEnabled ? "ເປີດຂາຍແລ້ວ" : "ປິດຂາຍແລ້ວ");
      } else {
        toast.success("ອັບເດດຈຳນວນແລ້ວ");
      }
      // รอให้ตารางรับค่าจาก server ก่อนล้าง draft และเปิดให้บันทึกครั้งถัดไป
      return qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
      });
    },
    onError: (err: Error, vars) => {
      mapSessionProductPatchError(
        err,
        vars.cfCode != null ? "ອັບເດດລະຫັດ CF ລົ້ມເຫຼວ" : "ອັບເດດຈຳນວນລົ້ມເຫຼວ",
      );
    },
  });
}

export function usePatchSessionProductsBulk(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ids: string[];
      patch: {
        allocatedQuantity?: number;
        allocationMode?: "limit" | "unlimited";
        discountPercent?: number | null;
        discountFixedLak?: number | null;
        saleEnabled?: boolean;
      };
    }) => feedsApi.patchPostSessionProductsBulk(input),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
      });
      if (data.items.length > 0) {
        const byId = new Map(data.items.map((item) => [item.id, item]));
        qc.setQueryData<PostProductsContextResponse>(
          feedsKeys.postContext(feedId),
          (old) =>
            old
              ? {
                  ...old,
                  sessionProducts: old.sessionProducts.map(
                    (item) => byId.get(item.id) ?? item,
                  ),
                }
              : old,
        );
      }
      if (data.failed.length > 0) {
        toast.error(`ອັບເດດສຳເລັດ ${data.updated} · ລົ້ມເຫຼວ ${data.failed.length}`);
      } else if (vars.patch.saleEnabled !== undefined) {
        toast.success(vars.patch.saleEnabled ? "ເປີດຂາຍແລ້ວ" : "ປິດຂາຍແລ້ວ");
      } else if (vars.patch.allocationMode != null) {
        toast.success("ອັບເດດໂຫມດໂຄວຕ້າແລ້ວ");
      } else {
        toast.success(`ອັບເດດ ${data.updated} ລາຍການແລ້ວ`);
      }
    },
    onError: (err: Error) => {
      mapSessionProductPatchError(err, "ອັບເດດຫຼາຍລາຍການລົ້ມເຫຼວ");
    },
  });
}

export function useRemoveSessionProduct(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionProductId: string) =>
      feedsApi.removeSessionProduct(sessionProductId),
    onSuccess: (_data, sessionProductId) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
      });
      qc.setQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(feedId),
        (old) =>
          old
            ? {
                ...old,
                sessionProducts: old.sessionProducts.filter(
                  (item) => item.id !== sessionProductId,
                ),
              }
            : old,
      );
      qc.setQueryData<{ items: { id: string }[] }>(
        [...feedsKeys.detail(feedId), "sessionProducts"],
        (old) =>
          old
            ? {
                ...old,
                items: old.items.filter((item) => item.id !== sessionProductId),
              }
            : old,
      );
      toast.success("ລຶບສິນຄ້າອອກຈາກໂພສແລ້ວ");
    },
    onError: () => toast.error("ລຶບສິນຄ້າລົ້ມເຫຼວ"),
  });
}

export function useRemoveSessionProductsBulk(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => feedsApi.removeSessionProductsBulk({ ids }),
    onSuccess: (data, ids) => {
      void qc.invalidateQueries({
        queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
      });
      const failedIds = new Set(data.failed.map((f) => f.id));
      const removedIds = new Set(ids.filter((id) => !failedIds.has(id)));
      qc.setQueryData<PostProductsContextResponse>(
        feedsKeys.postContext(feedId),
        (old) =>
          old
            ? {
                ...old,
                sessionProducts: old.sessionProducts.filter(
                  (item) => !removedIds.has(item.id),
                ),
              }
            : old,
      );
      if (data.failed.length > 0) {
        toast.error(`ລຶບສຳເລັດ ${data.removed} · ລົ້ມເຫຼວ ${data.failed.length}`);
      } else {
        toast.success(`ລຶບ ${data.removed} ລາຍການແລ້ວ`);
      }
    },
    onError: () => toast.error("ລຶບຫຼາຍລາຍການລົ້ມເຫຼວ"),
  });
}

export function useConfirmPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedId: string) => feedsApi.confirmPost(feedId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedsKeys.all });
      toast.success("ຢັ້ງຢືນໂພສສໍາເລັດ");
    },
    onError: () => toast.error("ຢັ້ງຢືນໂພສລົ້ມເຫຼວ"),
  });
}

export function useToggleFeedOrdering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      feedsApi.setOrdering(id, enabled),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: feedsKeys.all });
      if (data.warning === "NO_SESSION_PRODUCTS") {
        toast.success("ເປີດຮັບຄໍາສັ່ງແລ້ວ", {
          description: "ຍັງບໍ່ມີສິນຄ້າໃນ session — ເພີ່ມສິນຄ້າກ່ອນເພື່ອສ້າງອໍເດີ້ອັດຕະໂນມັດ",
        });
      } else if (data.warning === "PAGE_WEBHOOK_UNSUBSCRIBED") {
        toast.success("ປິດຮັບຄໍາສັ່ງແລ້ວ", {
          description: "ບໍ່ມີໂພສໃດເປີດຢູ່ — ປົດ webhook subscription ຂອງເພຈແລ້ວ",
        });
      } else {
        toast.success(
          data.feed.orderingEnabled ? "ເປີດຮັບຄໍາສັ່ງຈາກຄອມເມັນແລ້ວ" : "ປິດຮັບຄໍາສັ່ງແລ້ວ",
        );
      }
    },
    onError: () => toast.error("ປ່ຽນສະຖານະລົ້ມເຫຼວ"),
  });
}
